import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { usePageTitle } from "@/contexts/PageTitlesContext";

export default function Products() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const pt = usePageTitle("products", { title_zh: t("products.title"), title_en: t("products.title"), subtitle_zh: "collection", subtitle_en: "collection" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const active = searchParams.get("category") || "";

  useEffect(() => {
    api.get("/categories")
      .then((r) => setCategories(r.data))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (active) params.category = active;
    api.get("/products", { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [active]);

  const setCategory = (c) => {
    if (c) setSearchParams({ category: c });
    else setSearchParams({});
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="products-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold" data-testid="products-subtitle">
        {zh ? (pt.subtitle_zh || "collection") : (pt.subtitle_en || "collection")}
      </div>
      <h1 className="mt-3 font-serif-cn text-4xl md:text-5xl text-[#003645] font-semibold tracking-tight" data-testid="products-title">
        {zh ? pt.title_zh : pt.title_en}
      </h1>

      <div className="mt-10 flex flex-wrap gap-2" data-testid="category-filters">
        <button
          onClick={() => setCategory("")}
          className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
            !active
              ? "bg-[#005870] text-white border-[#005870]"
              : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e] hover:text-[#00868e]"
          }`}
          data-testid="cat-all"
        >
          {t("products.all")}
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            onClick={() => setCategory(c.slug)}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
              active === c.slug
                ? "bg-[#005870] text-white border-[#005870]"
                : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e] hover:text-[#00868e]"
            }`}
            data-testid={`cat-${c.slug}`}
          >
            {zh ? c.name_zh : c.name_en}
          </button>
        ))}
      </div>

      <div className="mt-12">
        {loading ? (
          <div className="text-[#4a6e77]">{t("common.loading")}</div>
        ) : products.length === 0 ? (
          <div className="text-[#4a6e77]">{t("products.empty")}</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
